import { Box, Typography, Skeleton } from "@mui/material";
import React, { useEffect, useState } from "react";
import { fetchOrderCountByCategory } from "../../services/orderAnalytics.service";
import { format2 } from "../../helpers/helper";

const SalesCategoryWise = ({ dateParams, isDateRangeIncomplete }) => {
    const [data, setData] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);

    const { from, to } = dateParams || {};

    useEffect(() => {
        if (isDateRangeIncomplete || !from || !to) {
            setData([]);
            setIsLoading(false);
            setError(null);
            return;
        }

        let isMounted = true;

        const loadData = async () => {
            try {
                setIsLoading(true);
                setError(null);

                const list = await fetchOrderCountByCategory({ from, to });
                if (!isMounted) return;

                const transformed = list.map(item => ({
                    label: item.id,
                    value: item.total || 0,
                }));

                setData(transformed);
            } catch (err) {
                if (isMounted) {
                    console.error("SalesCategoryWise API error", err);
                    setError("Unable to load sales data");
                    setData([]);
                }
            } finally {
                if (isMounted) setIsLoading(false);
            }
        };

        loadData();
        return () => {
            isMounted = false;
        };
    }, [from, to, isDateRangeIncomplete]);

    if (isLoading) {
        return (
            <Box
                sx={{
                    minHeight: 320,
                    p: { xs: 2.5, lg: 2 },
                    borderRadius: "20px",
                    bgcolor: "background.paper",
                    boxShadow: "0px 6px 20px rgba(0,0,0,0.35)",
                }}
            >
                <Typography sx={{ fontSize: { xs: 12, lg: 14 }, color: "secondary.main" }}>
                    Sales - Category wise
                </Typography>

                <Box sx={{ display: "flex", flexDirection: "column", gap: 3, mt: 2 }}>
                    {[1, 2, 3, 4].map(i => (
                        <Box key={i} sx={{ display: "flex", alignItems: "center" }}>
                            <Skeleton width={32} height={12} />
                            <Skeleton
                                variant="rectangular"
                                height={16}
                                width="100%"
                                sx={{ ml: 1 }}
                            />
                        </Box>
                    ))}
                </Box>
            </Box>
        );
    }

    if (error) {
        return (
            <Box
                sx={{
                    minHeight: 320,
                    p: { xs: 2.5, lg: 2 },
                    borderRadius: "20px",
                    bgcolor: "background.paper",
                    boxShadow: "0px 6px 20px rgba(0,0,0,0.35)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                }}
            >
                <Typography sx={{ color: "error.main", fontSize: 12 }}>
                    {error}
                </Typography>
            </Box>
        );
    }

    if (isDateRangeIncomplete) {
        return (
            <Box
                sx={{
                    minHeight: 320,
                    p: { xs: 2.5, lg: 2 },
                    borderRadius: "20px",
                    bgcolor: "background.paper",
                    boxShadow: "0px 6px 20px rgba(0,0,0,0.35)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                }}
            >
                <Typography sx={{ color: "warning.main", fontSize: 12 }}>
                    Select both From & To dates
                </Typography>
            </Box>
        );
    }

    if (!data.length) {
        return (
            <Box
                sx={{
                    minHeight: 320,
                    p: { xs: 2.5, lg: 2 },
                    borderRadius: "20px",
                    bgcolor: "background.paper",
                    boxShadow: "0px 6px 20px rgba(0,0,0,0.35)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                }}
            >
                <Typography sx={{ color: "text.secondary", fontSize: 12 }}>
                    No sales data available
                </Typography>
            </Box>
        );
    }

    const maxValue = Math.max(...data.map(d => d.value));

    return (
        <Box
            sx={{
                minHeight: 320,
                p: { xs: 2.5, lg: 2 },
                borderRadius: "20px",
                bgcolor: "background.paper",
                boxShadow: "0px 6px 20px rgba(0,0,0,0.35)",
                transition: "all 0.2s ease",
                "&:hover": { transform: "translateY(-2px)" },
            }}
        >
            <Typography sx={{ fontSize: { xs: 12, lg: 14 }, color: "secondary.main" }}>
                Sales - Category wise
            </Typography>

            <Box
                sx={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 3,
                    mt: 2,
                    flex: 1,
                    overflowY: "auto",
                }}
            >
                {data.map((item) => {
                    const widthPercent = (item.value / maxValue) * 100;
                    const canFitText = widthPercent >= 30;

                    return (
                        <Box key={item.label} sx={{ display: "flex", alignItems: "center" }}>
                            <Typography
                                sx={{
                                    width: 32,
                                    fontSize: "10px",
                                    fontWeight: 700,
                                    color: "text.secondary",
                                }}
                            >
                                {item.label}
                            </Typography>

                            <Box
                                sx={{
                                    flex: 1,
                                    height: { xs: 15, sm: 16, md: 17, lg: 18, xl: 20 },
                                    borderRadius: "4px",
                                    position: "relative",
                                    overflow: "hidden",
                                }}
                            >
                                <Box
                                    sx={{
                                        height: "100%",
                                        width: `${Math.max(widthPercent, 6)}%`,
                                        backgroundColor: "#059D98",
                                        borderRadius: "4px",
                                        display: "flex",
                                        alignItems: "center",
                                        pl: "10px",
                                        transition: "width 0.8s ease",
                                    }}
                                >
                                    <Typography
                                        sx={{
                                            fontSize: "10px",
                                            fontWeight: 500,
                                            color: canFitText
                                                ? "#FFFFFF"
                                                : "text.primary",
                                            whiteSpace: "nowrap",
                                        }}
                                    >
                                        {format2(item.value)}
                                    </Typography>
                                </Box>
                            </Box>
                        </Box>
                    );
                })}
            </Box>
        </Box>
    );
};

export default React.memo(SalesCategoryWise);